import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resourcesAPI } from '../../services/api';

export interface Resource {
  id: string;
  name: string;
  type?: string;
  description?: string;
  capacity: number;
  isActive: boolean;
  metadata?: any;
  photoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ResourcesState {
  requestId?: string;
  resources: Resource[];
  loading: boolean;
  error: string | null;
}

const initialState: ResourcesState = {
  resources: [],
  loading: false,
  error: null,
};

export const fetchResources = createAsyncThunk(
  'resources/fetchResources',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resourcesAPI.getAll();
      return response.data.resources;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la încărcarea resurselor');
    }
  }
);

export const createResource = createAsyncThunk(
  'resources/createResource',
  async (resourceData: Partial<Resource>, { rejectWithValue }) => {
    try {
      const response = await resourcesAPI.create(resourceData);
      return response.data.resource;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la crearea resursei');
    }
  }
);

export const updateResource = createAsyncThunk(
  'resources/updateResource',
  async ({ id, data }: { id: string; data: Partial<Resource> }, { rejectWithValue }) => {
    try {
      const response = await resourcesAPI.update(id, data);
      return response.data.resource;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la actualizarea resursei');
    }
  }
);

export const deleteResource = createAsyncThunk(
  'resources/deleteResource',
  async (id: string, { rejectWithValue }) => {
    try {
      await resourcesAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la ștergerea resursei');
    }
  }
);

const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setBookingResources: (state, action) => { state.resources = action.payload; },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResources.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.resources = action.payload;
        state.error = null;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createResource.fulfilled, (state, action) => {
        state.resources.push(action.payload);
      })
      .addCase(updateResource.fulfilled, (state, action) => {
        const index = state.resources.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.resources[index] = action.payload;
        }
      })
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.resources = state.resources.filter(r => r.id !== action.payload);
      });
  },
});

export const { clearError, setBookingResources } = resourcesSlice.actions;
export default resourcesSlice.reducer;
